// columnsConfig.js
import React from "react";
import { isEmpty, isNull, isUndefined } from "lodash";
import "../../SysGuard.scss";
import Popover from "@material-ui/core/Popover";
import { Chip, withStyles } from "@material-ui/core";
import { MFALookUp, threatLevelLookup } from "../../sysGuardConstants";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import SensitivityBadge from "../../../../components/shared/Badge/SensitivityBadge";
import { GOOGLEWORKSPACE } from "../../../../redux/constants/constants";
import { icons } from "../../../../components/common/utilities";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Tooltip from "@material-ui/core/Tooltip";

const ColoredTooltip = withStyles({
  tooltip: {
    fontSize: "14px",
    fontWeight: 500,
    backgroundColor: "#fff",
  },
})(Tooltip);

// Register English locale (needed for name recognition)
countries.registerLocale(enLocale);

export const getCountryCode = (countryName) => {
  return (
    countries.getAlpha2Code(countryName?.trim(), "en") || countryName?.trim()
  );
};

export const renderLocationClassName = (value) => {
  if (value === 0) {
    return "sys-guard-location green";
  } else if (value <= 10) {
    return "sys-guard-location normal";
  } else {
    return "sys-guard-location high";
  }
};

const renderNonAnomaliesClasses = (value, status) => {
  if (status === "DELETED") {
    return "sys-guard-location deleted";
  }
  return "sys-guard-location nonAnomalies";
};

export const renderRiskClassName = (value) => {
  const riskClasses = {
    high: "sys-guard-location highRisk",
    medium: "sys-guard-location mediumRisk",
    none: "sys-guard-location none",
  };

  return riskClasses[value?.toLowerCase()] || "sys-guard-location lowRisk";
};

export const renderNull = () => <>-</>;

export const getDataSourceIcon = (typeCode) => {
  return icons[typeCode];
};

const renderDomain = (params, sysGuardDataSource) => {
  if (!params.row.dataSourceId) {
    return renderNull();
  }

  // If datasourceId exists in row, show that specific datasource
  const datasourceId = params.row.dataSourceId;
  const ds = sysGuardDataSource[datasourceId];
  const deletedUser = params.row.status === "DELETED";

  if (!ds) {
    return renderNull();
  }

  return (
    <Chip
      key={ds.id}
      title={ds?.domainName}
      icon={
        <img
          src={getDataSourceIcon(ds?.typeCode)}
          alt={ds?.typeName}
          className="data-source-chip-icon"
        />
      }
      label={ds?.domainName}
      className={`data-source-chip ${deletedUser ? "deleted" : ""}`}
      size="small"
    />
  );
};

const renderCellWithClass = (params, classNameFn, onCellClick, loc) => {
  if (isNull(params.value)) {
    return renderNull();
  }

  return (
    <div
      className={
        !onCellClick
          ? classNameFn(params.value, params.row.status)
          : `${classNameFn(params.value, params.row.status)} pointer`
      }
      {...(onCellClick && {
        onClick: (e) => {
          e.stopPropagation(); // prevents accordion expand/collapse
          onCellClick(params.row, loc);
        },
      })}
    >
      {threatLevelLookup[params.value] ||
        (loc === "locationBasedFailurePercentage" ||
        loc === "cityBasedFailurePercentage" ||
        loc === "userBasedFailurePercentage" ||
        loc === "applicationBasedFailurePercentage"
          ? renderFailurePercentage(params)
          : params.value)}
    </div>
  );
};

const renderApplicationName = (params) => {
  if (isNull(params.value) || isEmpty(params.value)) {
    return <div>Unknown</div>;
  } else {
    return <div>{params.value}</div>;
  }
};

const renderMFA = (params) => {
  if (isNull(params.value)) {
    return renderNull();
  }

  const notApplicable =
    params.row?.status === "DELETED" && params.value === "false";

  return (
    <div
      className={
        notApplicable
          ? "grayText"
          : params.value === "false"
            ? "redText"
            : "greenText"
      }
      {...(params?.row?.acceptedRiskReason && {
        title: params.row.acceptedRiskReason,
      })}
    >
      {notApplicable ? "N/A" : MFALookUp[params.value]}
    </div>
  );
};

export const getFlagUrl = (code) =>
  `https://flagcdn.com/w40/${code.toLowerCase()}.png`;

export const renderCountryNames = (
  params,
  anchorEl,
  handlePopoverToggle,
  handlePopoverClose,
  popoverData,
) => {
  if (!params.value) return renderNull();

  const open = Boolean(anchorEl);

  const MIN_NO_COUNTRIES = 5;
  const countryNames = params.value.split(",").map((name) => name.trim());

  const shortCountryNames = countryNames.slice(0, MIN_NO_COUNTRIES);
  const remainingCountryNames = countryNames.slice(MIN_NO_COUNTRIES);
  const remainingCount = remainingCountryNames.length;

  const getCountryData = (names) =>
    names.map((name) => {
      const code = getCountryCode(name);
      const flagUrl =
        code && code !== "Unknown"
          ? getFlagUrl(code)
          : "/sysGuardIcons/unknownFlag.svg";
      return { name, code, flagUrl };
    });

  const shortCountryData = getCountryData(shortCountryNames);
  const remainingCountryData = getCountryData(remainingCountryNames);

  return (
    <div className="country-names-container sysguard">
      {shortCountryData.map((country, index) => (
        <div key={index} className="country-badge" title={country.name}>
          <img
            src={country.flagUrl}
            alt={country.name}
            className="country-flag"
          />
          <div className="country-code">{country.code || "N/A"}</div>
        </div>
      ))}

      {remainingCount > 0 && (
        <>
          <div className="remaining-image-count-sysguard-container">
            <div
              className="circle sysguard"
              onClick={(e) => handlePopoverToggle(e, remainingCountryData)}
            >
              <span className="circleText sysguard">{`+${remainingCount}`}</span>
            </div>
          </div>

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <div className="popover-country-list">
              {popoverData.map((country, index) => (
                <div key={index} className="country-badge" title={country.name}>
                  <img
                    src={country.flagUrl}
                    alt={country.name}
                    className="country-flag"
                  />
                  <div className="country-code">{country.code || "N/A"}</div>
                </div>
              ))}
            </div>
          </Popover>
        </>
      )}
    </div>
  );
};

const renderUserRisk = (params) => {
  if (isNull(params.value) || isEmpty(params.value)) {
    return renderNull();
  }
  const riskData = {
    sensitivity_count_high: params.value.HIGH,
    sensitivity_count_low: params.value.MEDIUM,
    sensitivity_count_medium: params.value.LOW,
    sensitivity_count_not_processed: params.value.NONE,
    total_sensitivity_count: params.row.numberOfUsers,
  };

  return (
    <div className="user-risk-container sysguard">
      <SensitivityBadge parentprops={riskData} loc="sysGuard" />
    </div>
  );
};

const renderFailurePercentage = ({ row }) => {
  const { failedAccess, totalAccess, failurePercentage } = row || {};

  if (
    isNull(failedAccess) ||
    isUndefined(failedAccess) ||
    isNull(totalAccess) ||
    isUndefined(totalAccess)
  ) {
    return renderNull();
  }

  return (
    <div
      title={`Failed: ${failedAccess} / Total: ${totalAccess} | Percentage: ${failurePercentage}%`}
    >
      {`${failedAccess}`}
    </div>
  );
};
const createRenderHeader =
  (
    headerName,
    field,
    isSelected,
    activeFilterField,
    handleFilterIconClick,
    showFilter = true,
  ) =>
  (params) => (
    <div className="tableHeaderCell">
      <span>{headerName}</span>
      {showFilter && (
        <span
          className={`tableFilterIcon ${
            isSelected || activeFilterField === field ? "visible" : ""
          }`}
          onClick={(e) => handleFilterIconClick(e, field)}
        >
          <img
            src={
              isSelected
                ? "/sysGuardIcons/filter_list.svg"
                : "/sysGuardIcons/filter_list_notSelected.svg"
            }
            alt="filter-icon"
          />
        </span>
      )}
    </div>
  );

const columnsConfig = (
  onCellClick,
  dataSourceType,
  appNamesFilter,
  countryNameFilter,
  threatFilter,
  userNameFilter,
  MFAFilter,
  UserRiskFilter,
  handleFilterIconClick,
  activeFilterField,
  isIncognito,
  anchorEl,
  handlePopoverToggle,
  handlePopoverClose,
  popoverData,
  sysGuardDataSource,
) => {
  const deviceColumn =
    dataSourceType === GOOGLEWORKSPACE
      ? {
          field: "totalNoOfDevices",
          headerName: "Devices",
          flex: 1,
          renderCell: (params) =>
            renderCellWithClass(
              params,
              renderNonAnomaliesClasses,
              onCellClick,
              "totalDevices",
            ),
          align: "center",
          headerAlign: "center",
        }
      : {
          field: "noOfDeviceAnomalies",
          headerName: "Device Anomalies",
          flex: 1,
          renderCell: (params) =>
            renderCellWithClass(
              params,
              renderLocationClassName,
              onCellClick,
              "userDevices",
            ),
          align: "center",
          headerAlign: "center",
        };

  return {
    Locations: [
      {
        field: "countryName",
        headerName: "Country Name",
        flex: 0.8,
        renderHeader: createRenderHeader(
          "Country Name",
          "countryName",
          countryNameFilter?.length,
          activeFilterField,
          handleFilterIconClick,
        ),
      },
      {
        field: "numberOfUsers",
        headerName: "No of Users",
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "locationBasedUsers",
          ),
      },
      {
        field: "risk",
        headerName: "User Risk",
        flex: 1.5,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => renderUserRisk(params),
      },
      {
        field: "locationAnomalies",
        headerName: "Location Based Anomalies",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderLocationClassName,
            onCellClick,
            "locationBasedAnomalies",
          ),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "failurePercentage",
        headerName: "Failed Attempts",
        flex: 1.2,
        align: "center",
        headerAlign: "center",
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "locationBasedFailurePercentage",
          ),
      },
      {
        field: "applicationInUse",
        headerName: "No of Applications",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "locationBasedApplications",
          ),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "countryRisk",
        headerName: "Threat Probability",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(params, renderRiskClassName),
        align: "center",
        headerAlign: "center",
        renderHeader: createRenderHeader(
          "Threat Probability",
          "countryRisk",
          threatFilter?.length,
          activeFilterField,
          handleFilterIconClick,
        ),
      },
    ],
    Cities: [
      {
        field: "cityName",
        headerName: "City` Name",
        flex: 0.8,
        renderHeader: createRenderHeader(
          "City Name",
          "cityName",
          countryNameFilter?.length,
          activeFilterField,
          handleFilterIconClick,
        ),
      },
      {
        field: "numberOfUsers",
        headerName: "No of Users",
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "cityBasedUsers",
          ),
      },
      {
        field: "risk",
        headerName: "User Risk",
        flex: 1.5,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => renderUserRisk(params),
      },
      {
        field: "locationAnomalies",
        headerName: "Location Based Anomalies",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderLocationClassName,
            onCellClick,
            "cityBasedAnomalies",
          ),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "failurePercentage",
        headerName: "Failed Attempts",
        flex: 1.2,
        align: "center",
        headerAlign: "center",
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "cityBasedFailurePercentage",
          ),
      },
      {
        field: "applicationInUse",
        headerName: "No of Applications",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "cityBasedApplications",
          ),
        align: "center",
        headerAlign: "center",
      },
    ],
    Users: [
      {
        field: "userName",
        headerName: "User Name",
        flex: 1,
        filterable: !isIncognito,
        renderHeader: createRenderHeader(
          "User Name",
          "userName",
          userNameFilter?.length,
          activeFilterField,
          handleFilterIconClick,
          !isIncognito,
        ),
        renderCell: (params) => (
          <div
            className="user-name-cell"
            {...(!isIncognito && { title: params.row.emailId })}
          >
            {params.value}
          </div>
        ),
      },
      {
        field: "userType",
        headerName: "Type",
        flex: 1,
        renderCell: (params) => (<span>{params.value}</span>),
      },
      {
        field: "Domain",
        headerName: "Domain",
        flex: 1,
        filterable: !isIncognito,
        renderCell: (params) => renderDomain(params, sysGuardDataSource),
      },
      {
        field: "locations",
        headerName: "Locations",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "userLocations",
          ),
        align: "center",
        headerAlign: "center",
      },
      deviceColumn,
      {
        field: "mfa",
        headerName: "MFA",
        flex: 1,
        renderCell: (params) => renderMFA(params),
        align: "center",
        headerAlign: "center",
        renderHeader: createRenderHeader(
          "MFA",
          "mfa",
          MFAFilter?.length,
          activeFilterField,
          handleFilterIconClick,
        ),
      },
      {
        field: "behavioralAnomaly",
        headerName: "Behavioral Anomalies",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderLocationClassName,
            onCellClick,
            "userAnomalies",
          ),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "failurePercentage",
        headerName: "Failed Attempts",
        flex: 1.2,
        align: "center",
        headerAlign: "center",
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "userBasedFailurePercentage",
          ),
      },
      {
        field: "applicationsInUse",
        headerName: "No of Applications",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "userApplications",
          ),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "userRisk",
        headerName: "Risk Factor",
        flex: 1,
        renderCell: (params) =>
          typeof params.value === "string"
            ? renderCellWithClass(params, renderRiskClassName)
            : renderNull(),
        align: "center",
        headerAlign: "center",
        renderHeader: createRenderHeader(
          "Risk Factor",
          "userRisk",
          UserRiskFilter?.length,
          activeFilterField,
          handleFilterIconClick,
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.5,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const status = params.value;

          const statusConfig = {
            ACTIVE: {
              icon: CheckCircleOutlineIcon,
              color: "green",
              label: "Enabled",
            },
            DELETED: {
              icon: DeleteOutlineIcon,
              color: "black",
              label: "Deleted",
            },
            INACTIVE: {
              icon: HighlightOffIcon,
              color: "red",
              label: "Disabled",
            },
            Unknown: {
              icon: HelpOutlineIcon,
              color: "orange",
              label: "Unknown",
            },
          };

          const config = statusConfig[status];

          if (!config) return null;

          const IconComponent = config.icon;

          return (
            <span title={config.label}>
              <IconComponent style={{ color: config.color }} />
            </span>
          );
        },
      },
    ],
    Applications: [
      {
        field: "applicationName",
        headerName: "Application Name",
        flex: 1,
        renderCell: (params) => renderApplicationName(params),
        renderHeader: createRenderHeader(
          "Application Name",
          "applicationName",
          appNamesFilter?.length,
          activeFilterField,
          handleFilterIconClick,
        ),
      },
      {
        field: "numberOfUsers",
        headerName: "No of Users",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "applicationBasedUsers",
          ),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "risk",
        headerName: "User Risk",
        flex: 1,
        renderCell: (params) => renderUserRisk(params),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "applicationAnomalies",
        headerName: "Application Anomalies",
        flex: 1,
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderLocationClassName,
            onCellClick,
            "applicationBasedAnomalies",
          ),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "failurePercentage",
        headerName: "Failed Attempts",
        flex: 1.2,
        align: "center",
        headerAlign: "center",
        renderCell: (params) =>
          renderCellWithClass(
            params,
            renderNonAnomaliesClasses,
            onCellClick,
            "applicationBasedFailurePercentage",
          ),
      },
      {
        field: "countryNames",
        headerName: "Accessed from location",
        flex: 1.3,
        renderCell: (params) =>
          renderCountryNames(
            params,
            anchorEl,
            handlePopoverToggle,
            handlePopoverClose,
            popoverData,
          ),
        renderHeader: createRenderHeader(
          "Accessed from location",
          "countryNames",
          countryNameFilter?.length,
          activeFilterField,
          handleFilterIconClick,
        ),
      },
    ],
  };
};

export default columnsConfig;
